import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, Plus, Eye, Pencil, Globe, Lock, Share2 } from 'lucide-react';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn } from '@ui-admin/components/DataGrid/types';
import type { ReportDefinitionLocal } from '@firesquirrel/local-db';
import { listReportDefinitions, toggleLibrary, seedToTenant } from '../../services/systemReportDefinitionService';
import SeedToTenantModal from '../../components/SeedToTenantModal';

const columns: DataGridColumn<ReportDefinitionLocal>[] = [
  {
    id: 'name',
    header: 'Dashboard Name',
    accessorKey: 'name',
    cell: (info) => {
      const row = info.row.original;
      return (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          {row.description && (
            <div className="text-sm text-gray-500">{row.description}</div>
          )}
        </div>
      );
    },
  },
  {
    id: 'scope',
    header: 'Scope',
    cell: (info) => {
      const row = info.row.original;
      const isSystem = row.scope?.level === 'system';
      return (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            isSystem
              ? 'bg-purple-100 text-purple-800'
              : 'bg-blue-100 text-blue-800'
          }`}
        >
          {isSystem ? 'System' : 'Dept'}
        </span>
      );
    },
  },
  {
    id: 'widgets',
    header: 'Widgets',
    cell: (info) => info.row.original.dashboardConfig?.widgets?.length || 0,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    cell: (info) => {
      const row = info.row.original;
      const statusColors: Record<string, string> = {
        draft: 'bg-gray-100 text-gray-800',
        published: 'bg-green-100 text-green-800',
        archived: 'bg-yellow-100 text-yellow-800',
      };
      return (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            statusColors[row.status] || 'bg-gray-100 text-gray-800'
          }`}
        >
          {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Unknown'}
        </span>
      );
    },
  },
  {
    id: 'library',
    header: 'Library',
    cell: (info) => {
      const row = info.row.original;
      if (row.scope?.level !== 'system') return <span className="text-gray-300">—</span>;
      return row.isInLibrary
        ? <span className="inline-flex items-center gap-1 text-xs text-green-700"><Globe className="h-3.5 w-3.5" /> In Library</span>
        : <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Lock className="h-3.5 w-3.5" /> Not in Library</span>;
    },
  },
  {
    id: 'updatedAt',
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: (info) => new Date(info.row.original.updatedAt).toLocaleDateString(),
  },
];

export default function SystemDashboardsPage() {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState<ReportDefinitionLocal[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState<string>('all');
  const [seedModalOpen, setSeedModalOpen] = useState(false);
  const [selectedDashboard, setSelectedDashboard] = useState<ReportDefinitionLocal | null>(null);

  useEffect(() => {
    loadDashboards();
  }, [selectedTenant]);

  async function loadDashboards() {
    setLoading(true);
    try {
      const params = selectedTenant === 'all' 
        ? { type: 'dashboard' as const }
        : selectedTenant === 'system'
        ? { type: 'dashboard' as const, includeSystem: true, tenantId: undefined }
        : { type: 'dashboard' as const, tenantId: selectedTenant };
      
      const data = await listReportDefinitions(params);
      setDashboards(data);
    } catch (error) {
      console.error('[SystemDashboardsPage] Failed to load dashboards:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleRefresh = async () => {
    await loadDashboards();
  };

  if (loading) return null;

  return (
    <>
      <GridPageShell
      gridId="system-dashboards"
      gridType="dashboards"
      title="Dashboard Templates"
      subtitle={`${dashboards.length} ${dashboards.length === 1 ? 'template' : 'templates'}`}
      columns={columns}
      data={dashboards}
      getRowId={(row) => row._id}
      searchPlaceholder="Search dashboard templates..."
      toolbarActions={
        <div className="flex items-center gap-3">
          <select
            title="Filter by department"
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Departments</option>
            <option value="system">System Only</option>
          </select>
          <button
            onClick={() => navigate('/dashboard-templates/new')}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-white bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Dashboard Template
          </button>
        </div>
      }
      onRefresh={handleRefresh}
      emptyIcon={LayoutDashboard}
      emptyTitle="No dashboard templates"
      emptySubtitle="Create your first system-level dashboard template"
      emptyCTA={{ label: 'New Dashboard Template', onClick: () => navigate('/dashboard-templates/new') }}
      rowActions={[
        {
          id: 'view',
          label: 'View',
          icon: Eye,
          onClick: (row) => navigate(`/dashboard-templates/${row._id}/view`),
        },
        {
          id: 'edit',
          label: 'Edit',
          icon: Pencil,
          onClick: (row) => navigate(`/dashboard-templates/${row._id}`),
        },
        {
          id: 'addToLibrary',
          label: 'Add to Library',
          icon: Globe,
          onClick: async (row) => {
            await toggleLibrary(row._id, true);
            await loadDashboards();
          },
          hidden: (row) => !!row.scope?.tenantId || !!row.isInLibrary,
        },
        {
          id: 'removeFromLibrary',
          label: 'Remove from Library',
          icon: Lock,
          onClick: async (row) => {
            await toggleLibrary(row._id, false);
            await loadDashboards();
          },
          hidden: (row) => !!row.scope?.tenantId || !row.isInLibrary,
        },
        {
          id: 'seedToDepartments',
          label: 'Seed to Departments',
          icon: Share2,
          onClick: (row) => {
            setSelectedDashboard(row);
            setSeedModalOpen(true);
          },
          hidden: (row) => row.scope?.level !== 'system' || !row.isInLibrary,
        },
      ]}
      onRowClick={(row) => navigate(`/dashboard-templates/${row._id}/view`)}
      />
      {selectedDashboard && (
        <SeedToTenantModal
          isOpen={seedModalOpen}
          onClose={() => {
            setSeedModalOpen(false);
            setSelectedDashboard(null);
          }}
          onSeed={(tenantIds) => seedToTenant(selectedDashboard._id, tenantIds)}
          templateName={selectedDashboard.name}
          templateType="dashboard"
        />
      )}
    </>
  );
}
